import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorNote, Loading, PageBody, PageHead, Select, Tabs } from '../../components/aa';
import { useAsync } from '../aai/useAsync';
import { adoptionApi, isClientView, sumStats } from './adoption';
import type { AdoptionOverview, CompanyView } from './adoption';
import { CompanyAdoption, useRoot } from './CompanyAdoption';
import { ClientTable, OpportunityList, SessionList, Tiles } from './parts';
import { GraphPanel } from './GraphPanel';

type Tab = 'opportunities' | 'sessions' | 'clients' | 'graph';

const subFor = (kind: string): string => {
  if (kind === 'partner') return 'Your clients: the sessions you sent, the opportunities found, and what they turned into.';
  return 'Your company and the clients you serve: sessions, opportunities, and what they turned into.';
};

const GraphTab: React.FC<{ companies: CompanyView[]; selected: string; onSelect: (id: string) => void }> = ({ companies, selected, onSelect }) => {
  let company = companies.find((c) => c.id === selected);
  if (!company) company = companies[0];
  if (!company) return null;
  return (
    <div>
      <label className="mb-4 flex flex-wrap items-center gap-3 text-[13px] font-semibold text-ink-muted">
        Knowledge graph for
        <Select value={company.id} onChange={(e) => onSelect(e.target.value)} className="!h-10 !w-[260px]" aria-label="Company to show the knowledge graph for">
          {companies.map((c) => {
            let label = c.name;
            if (c.own) label = `${c.name} (your company)`;
            return <option key={c.id} value={c.id}>{label}</option>;
          })}
        </Select>
      </label>
      <GraphPanel key={company.id} companyId={company.id} companyName={company.name} />
    </div>
  );
};

const Overview: React.FC<{ data: AdoptionOverview; root: string }> = ({ data, root }) => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const companyFilter = params.get('company') || '';
  const companies = useMemo(() => {
    if (!companyFilter) return data.companies;
    return data.companies.filter((c) => c.id === companyFilter);
  }, [data.companies, companyFilter]);
  const clientView = isClientView(data.viewer);
  const stats = sumStats(companies);

  const opportunityCount = companies.reduce((n, c) => n + c.opportunities.length, 0);
  const sessionCount = companies.reduce((n, c) => n + c.sessions.length, 0);
  const tabs = [
    { id: 'opportunities', label: 'Opportunities', count: opportunityCount },
    { id: 'sessions', label: 'Sessions', count: sessionCount },
    { id: 'clients', label: 'Clients', count: companies.filter((c) => !c.own).length },
    { id: 'graph', label: 'Knowledge graph' },
  ];
  let tab = (params.get('tab') || 'opportunities') as Tab;
  if (!tabs.some((t) => t.id === tab)) tab = 'opportunities';

  const update = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    if (!value) next.delete(key);
    setParams(next, { replace: true });
  };
  const open = (id: string) => navigate(`${root}/${id}`);
  const answersLink = (companyId: string, sessionId: string): string | null => {
    const company = data.companies.find((c) => c.id === companyId);
    if (!company || !company.can_send) return null;
    return `${root}/${companyId}?tab=sessions&session=${encodeURIComponent(sessionId)}`;
  };

  return (
    <>
      <PageHead
        title="AI adoption"
        sub={subFor(data.viewer.kind)}
        actions={data.companies.length > 1 && (
          <label className="flex items-center gap-2 text-[12.5px] font-semibold text-ink-muted">
            <span className="sr-only">Show one company</span>
            <Select value={companyFilter} onChange={(e) => update('company', e.target.value)} className="!h-10 !w-[240px]" aria-label="Show one company">
              <option value="">All companies ({data.companies.length})</option>
              {data.companies.map((c: CompanyView) => {
                let label = c.name;
                if (c.own) label = `${c.name} (your company)`;
                return <option key={c.id} value={c.id}>{label}</option>;
              })}
            </Select>
          </label>
        )}
      />
      <Tiles stats={stats} clientView={clientView} />
      <Tabs tabs={tabs} active={tab} onChange={(id) => update('tab', id)} label="AI adoption" variant="pills" />
      {tab === 'opportunities' && (
        <OpportunityList companies={companies} clientView={clientView} showCompany={companies.length > 1} root={root} />
      )}
      {tab === 'sessions' && <SessionList companies={companies} showCompany={companies.length > 1} answersLink={answersLink} />}
      {tab === 'clients' && <ClientTable companies={companies} onOpen={open} />}
      {tab === 'graph' && <GraphTab companies={data.companies} selected={companyFilter} onSelect={(id) => update('company', id)} />}
    </>
  );
};

const AdoptionPage: React.FC = () => {
  const root = useRoot();
  const overview = useAsync<AdoptionOverview>(() => adoptionApi.overview(), 'Could not load AI adoption.', []);

  let body: React.ReactNode = <Loading label="Loading AI adoption" />;
  if (overview.error) body = <ErrorNote message={overview.error} />;
  const data = overview.data;
  if (data && !data.viewer.has_clients && !data.companies[0]) body = <ErrorNote message="There is no company to show yet." />;
  if (data && !data.viewer.has_clients && data.companies[0]) {
    body = <CompanyAdoption companyId={data.companies[0].id} root={root} embedded />;
  }
  if (data && data.viewer.has_clients) body = <Overview data={data} root={root} />;
  return <PageBody>{body}</PageBody>;
};

export default AdoptionPage;
